import React from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiTool, FiBox } from 'react-icons/fi';
import { IconType } from 'react-icons';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

interface Step {
  number: number;
  title: string;
  description: string;
  icon: IconType | string;
  color: string;
}

interface HowItWorksSectionProps {
  title?: string;
  steps?: Step[];
  darkMode: boolean;
  inView?: boolean;
  appName?: string;
}

export const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({
  title = "How It Works",
  steps = [
    {
      number: 1,
      title: "Select Personas",
      description: "Choose from our library of specialized AI personas or create your own custom personas with specific expertise and capabilities.",
      icon: FiUsers,
      color: "blue"
    },
    {
      number: 2,
      title: "Define Workflow",
      description: "Set up a structured workflow with multiple stages, assign personas to each stage, and define the inputs and outputs for each step.",
      icon: FiTool,
      color: "purple"
    },
    {
      number: 3,
      title: "Generate Artifacts",
      description: "Execute the workflow to generate high-quality artifacts such as code, documentation, diagrams, and more, ready for export and use.",
      icon: FiBox,
      color: "green"
    }
  ],
  darkMode,
  inView = true,
  appName = "The Solution Factory"
}) => {
  // Helper function to get icon component
  const getIconComponent = (icon: IconType | string) => {
    if (typeof icon === 'string') {
      // Map string icon names to components
      const iconMap = {
        'FiUsers': FiUsers,
        'FiTool': FiTool,
        'FiBox': FiBox
      };
      return iconMap[icon] || FiUsers;
    }
    return icon;
  };

  return (
    <div className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.h2 
          className={`text-3xl font-bold mb-12 text-center ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          How {appName} Works
        </motion.h2>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {steps.map((step) => {
            const Icon = getIconComponent(step.icon);
            return (
              <motion.div 
                key={step.number}
                className={`p-6 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} relative shadow-xl`}
                variants={itemVariants}
                whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
              >
                <motion.div 
                  className={`absolute -top-4 -left-4 w-10 h-10 rounded-full bg-${step.color}-600 text-white flex items-center justify-center font-bold text-lg`}
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1, rotate: 360 } : { scale: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 * step.number }}
                >
                  {step.number}
                </motion.div>
                <div className={`w-16 h-16 mb-4 rounded-lg ${darkMode ? `bg-${step.color}-900 text-${step.color}-300` : `bg-${step.color}-100 text-${step.color}-700`} flex items-center justify-center`}>
                  <Icon className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};
